/**
 * TabNavigationProvider - Provides Tab Navigator context to sidebar
 *
 * This component must be rendered INSIDE a Tab.Navigator to have access
 * to the correct navigation object for tab screens
 */

import React, { useMemo, ReactNode } from 'react';
import { useNavigation, useRoute, useNavigationState } from '@react-navigation/native';
import { NavigationProvider, type NavigationHelpers } from '@/layouts/NavigationContext';

interface TabNavigationProviderProps {
  children: ReactNode;
}

export const TabNavigationProvider: React.FC<TabNavigationProviderProps> = ({ children }) => {
  const navigation = useNavigation<any>(); // This is Tab Navigator's navigation
  const route = useRoute();

  // Get current tab route from navigation state
  const currentTabRoute =
    useNavigationState(state => {
      if (!state || typeof state.index !== 'number') {
        return 'Home';
      }
      const currentRoute = state.routes[state.index];
      return currentRoute?.name || 'Home';
    }) ?? 'Home';

  // Get current group ID if in GroupChat
  const currentGroupId =
    useNavigationState(state => {
      if (!state || typeof state.index !== 'number') {
        return null;
      }

      const currentRoute = state.routes[state.index];
      if (!currentRoute || currentRoute.name !== 'Groups') {
        return null; // Not in Groups tab
      }

      const groupsState = currentRoute.state;
      if (!groupsState || typeof groupsState.index !== 'number') {
        return null;
      }

      const currentGroupsScreen = groupsState.routes[groupsState.index];
      if (!currentGroupsScreen || currentGroupsScreen.name !== 'GroupChat') {
        return null; // Not in GroupChat screen
      }

      const groupId = (currentGroupsScreen.params as any)?.groupId;
      return typeof groupId === 'string' ? groupId : null;
    }) ?? null;

  // Create navigation helpers - these now use Tab Navigator's navigation object
  const navigationHelpers: NavigationHelpers = useMemo(
    () => ({
      navigateToHome: () => {
        console.log('[TabNav] Navigate to Home');
        navigation.navigate('Home');
      },
      navigateToResearch: () => {
        console.log('[TabNav] Navigate to Research');
        navigation.navigate('Research');
      },
      navigateToWatchlist: () => {
        console.log('[TabNav] Navigate to Watchlist');
        navigation.navigate('Watchlist');
      },
      navigateToGroups: () => {
        console.log('[TabNav] Navigate to Groups');
        navigation.navigate('Groups');
      },
      navigateToGroupChat: (groupId: string, groupName: string) => {
        console.log('[TabNav] Navigate to GroupChat:', groupId, groupName);
        navigation.navigate('Groups', {
          screen: 'GroupChat',
          params: { groupId, groupName },
        });
      },
      navigateToFeedback: () => {
        console.log('[TabNav] Navigate to Feedback');
        navigation.navigate('Feedback');
      },
      navigateToProfile: () => {
        console.log('[TabNav] Navigate to Profile');
        navigation.navigate('Profile');
      },
      getCurrentRoute: () => {
        return route.name;
      },
      currentTabRoute,
      currentGroupId,
    }),
    [navigation, route.name, currentTabRoute, currentGroupId]
  );

  return <NavigationProvider helpers={navigationHelpers}>{children}</NavigationProvider>;
};
